/**
 * OWA-998: what the user sees of task saves the server refused as stale (plan steps 19 to 22).
 * The saves stay in this browser's queue as held groups (heldTaskSaves.ts); this module shows
 * them and offers the three choices:
 * - View the New Task: open the task editor on the task as it is now;
 * - Merge (or Restore It with My Changes when the task was deleted): TaskMerge's two-version mode;
 * - Abandon: retire exactly the group's saves. Nothing here ever deletes the task.
 *
 * The notice is a dialog in the detached dialog root (#portal-root), so it survives navigation.
 * Needs Attention and the task editor's banner show the same groups from IndexedDB.
 */
import {FC, ReactNode, useCallback, useEffect, useState} from 'react';
import {Button} from '@mui/material';

import Task from '@/entities/task';
import store from '@/store/store';
import {dialogStore} from '@/store/zstore';
import {DialogType} from '@/store/dialogs';
import {dialogNoButtons} from '@/ui/Confirm';
import Alert from '@/lib/alert';
import {getSessionFirmId, getSessionUserId} from '@/lib/picos/sessionIdentity';
import {readBaseVersion, versionRef} from '@/lib/staleSave';
import {abandonHeldGroup, observeHeldGroups, saveMergedGroup, type HeldGroup} from './heldTaskSaves';
import {ABANDON, MERGE, RESTORE_WITH_MY_CHANGES, VIEW_THE_NEW_TASK} from './heldTaskText';
import './HeldTaskNotice.less';

/** How long to wait before following the queue again when it is not open yet. */
const FOLLOW_RETRY_MS = 2_000;
const FOLLOW_ATTEMPTS = 30;

/**
 * The held groups of this tab's user, following the queue in IndexedDB. Null until the queue has
 * been read once (and while there is no signed-in user).
 */
export function useHeldTaskGroups(): HeldGroup[] | null {
    const owner = JSON.stringify([getSessionFirmId(), getSessionUserId()]);
    const [observed, setObserved] = useState<{owner: string, groups: HeldGroup[]} | null>(null);

    useEffect(() => {
        if (!getSessionFirmId() || !getSessionUserId()) return;
        let stop: (() => void) | null = null;
        let retry: ReturnType<typeof setTimeout> | undefined;
        let attempts = 0;
        const follow = () => {
            stop = observeHeldGroups(groups => setObserved({owner, groups}));
            // The queue opens at sign-in; follow it once it is there
            if (!stop && ++attempts < FOLLOW_ATTEMPTS) retry = setTimeout(follow, FOLLOW_RETRY_MS);
        };
        follow();
        return () => {
            clearTimeout(retry);
            stop?.();
        };
    }, [owner]);

    return observed?.owner === owner ? observed.groups : null;
}

/** The group's key: the queue key of its refused save. */
export const heldKey = (group: HeldGroup): number => group.held.addedOn;

const isGone = (group: HeldGroup): boolean => !group.current || group.current.status === 'D';

/** The task's name as the user last saw it. */
const titleOf = (group: HeldGroup): string => group.newest.title || group.current?.title || 'This task';

/** The notice's sentence (plan step 19). */
export function noticeMessage(group: HeldGroup): ReactNode {
    const title = <strong>{titleOf(group)}</strong>;
    return isGone(group)
        ? <>{title} was deleted elsewhere.</>
        : <>{title} was changed elsewhere after you started editing it. Your changes were not saved.</>;
}

/** Needs Attention's reason for a held group. */
export const reasonOf = (group: HeldGroup): string => (isGone(group)
    ? 'Changes not saved: this task was deleted elsewhere.'
    : 'Changes not saved: this task was changed elsewhere after you started editing it.');

const newerAccepted = (row: Task, than: Task): boolean => {
    const rowDb = Number(row.dbAddedOn ?? 0), thanDb = Number(than.dbAddedOn ?? 0);
    return rowDb > thanDb || (rowDb === thanDb && row.addedOn > than.addedOn);
};

/**
 * The task as it is now: the refusal's copy, or a newer accepted version this tab has received
 * since (plan step 15). It names itself as its base, so an edit of it is checked against it.
 */
export function currentOf(group: HeldGroup): Task | null {
    const refused = group.current;
    if (!refused) return null;
    const live: Task | undefined = store.getState()?.tasks?.get?.(refused.id);
    const current = live && !live.pendingOp && live.dbAddedOn && newerAccepted(live, refused) ? live : refused;
    if (readBaseVersion(current.baseVersion).kind !== 'absent') return current;
    const based = current.clone();
    based.baseVersion = versionRef(current as Task & {addedOn: number}) as Task['baseVersion'];
    return based;
}

/** View the New Task (plan step 20): the task editor on the task as it is now; a deleted task opens read-only. */
export async function viewHeldGroup(group: HeldGroup): Promise<void> {
    const current = currentOf(group);
    if (!current) return;
    if (current.status !== 'D') {
        const {editTask} = await import('./TaskHelper');
        editTask(current);
        return;
    }
    const close = () => dialogStore.actions.close();
    dialogStore.actions.openTypedDialog(DialogType.Task, {
        title: 'Task (deleted)',
        fitWidth: false,
        fitHeight: true,
        componentProps: {current, embedded: true, viewOnly: true, trashReadOnly: true, onOk: close, onCancel: close},
        useCancelButton: true,
        cancelButtonLabel: 'Close',
    });
}

/**
 * Merge (plan step 21): TaskMerge between the group's newest version and the task as it is now.
 * The result is queued as an edit of the task as it is now (a restore when it was deleted) and,
 * once accepted, retires exactly the group's saves.
 */
export async function mergeHeldGroup(group: HeldGroup): Promise<void> {
    const current = currentOf(group);
    if (!current) return;
    const target: HeldGroup = {...group, current};
    const {default: TaskMerge} = await import('./TaskMerge/TaskMerge');
    const onMerge = (merged: Task) => {
        try {
            saveMergedGroup(target, merged);
        }
        catch (err) {
            console.error('OWA-998: could not save the merged task', err);
            Alert.addSimpleError('The merged task could not be saved. Your changes are still kept; try again.');
        }
    };
    dialogNoButtons(current.status === 'D' ? 'Restore Task' : 'Merge Changes',
        <TaskMerge held={{mine: group.newest, current, base: group.base}} onMerge={onMerge}/>,
        {maxWidth: '48em'});
}

/** Abandon (plan step 22): retire exactly the group's saves. The task is never deleted. */
export async function abandonHeld(group: HeldGroup): Promise<boolean> {
    const done = await abandonHeldGroup(group);
    if (!done) Alert.addSimpleError('Your unsaved changes could not be abandoned. Try again.');
    return done;
}

type ChoicesProps = {
    group: HeldGroup,
    /** The editor banner leaves out View: the user is already looking at the task. */
    withView?: boolean,
    /** Called when the user has made a choice (the notice closes). */
    onChosen?: () => void,
};

/** The three choices for a held group. */
export const HeldTaskChoices: FC<ChoicesProps> = ({group, withView = true, onChosen}) => {
    const [busy, setBusy] = useState(false);
    const exists = !!group.current;

    const onView = useCallback(() => {
        onChosen?.();
        void viewHeldGroup(group);
    }, [group, onChosen]);
    const onMerge = useCallback(() => {
        onChosen?.();
        void mergeHeldGroup(group);
    }, [group, onChosen]);
    const onAbandon = useCallback(async () => {
        setBusy(true);
        const done = await abandonHeld(group);
        setBusy(false);
        if (done) onChosen?.();
    }, [group, onChosen]);

    return (
        <div className='held-task-choices'>
            {withView && exists && <Button size='small' variant='text' onClick={onView}>{VIEW_THE_NEW_TASK}</Button>}
            {exists && <Button size='small' variant='text' color='success' onClick={onMerge}>
                {group.current?.status === 'D' ? RESTORE_WITH_MY_CHANGES : MERGE}
            </Button>}
            <Button size='small' variant='text' disabled={busy} onClick={onAbandon}>{ABANDON}</Button>
        </div>
    );
};

/** The notice dialog's content. It closes itself once its group is resolved anywhere. */
export const HeldTaskNotice: FC<{group: HeldGroup, onClose?: () => void}> = ({group, onClose}) => {
    const groups = useHeldTaskGroups();
    const live = groups?.find(g => heldKey(g) === heldKey(group));
    const resolved = !!groups && !live;

    useEffect(() => {
        if (resolved) onClose?.();
    }, [resolved, onClose]);

    const shown = live ?? group;
    return (
        <div className='held-task-notice'>
            <p>{noticeMessage(shown)}</p>
            <HeldTaskChoices group={shown} onChosen={onClose}/>
        </div>
    );
};

/** Groups this tab has already announced. */
const announced = new Set<number>();

/** Put up the notice for a held group, in the detached dialog root. */
export function showHeldTaskNotice(group: HeldGroup): void {
    dialogNoButtons('Changes Not Saved', <HeldTaskNotice group={group}/>, {maxWidth: '36em'});
}

/** Mounted once in the app: announces each held group when it appears (plan step 19). */
export const HeldTaskNotices: FC = () => {
    const groups = useHeldTaskGroups();
    useEffect(() => {
        for (const group of groups ?? []) {
            if (announced.has(heldKey(group))) continue;
            announced.add(heldKey(group));
            showHeldTaskNotice(group);
        }
    }, [groups]);
    return null;
};

/** The task editor's banner while this browser holds refused saves of the task (plan step 20). */
export const HeldTaskBanner: FC<{taskId: string}> = ({taskId}) => {
    const groups = useHeldTaskGroups();
    const mine = (groups ?? []).filter(group => group.held.id === taskId);
    if (mine.length === 0) return null;
    return <>
        {mine.map(group => (
            <div key={heldKey(group)} className='held-task-banner' role='status'>
                <span>{isGone(group)
                    ? 'This task was deleted elsewhere. Your changes to it were not saved.'
                    : 'Your changes to this task were not saved because it was changed elsewhere after you started editing it.'}</span>
                <HeldTaskChoices group={group} withView={false}/>
            </div>
        ))}
    </>;
};
